import { useState, useEffect } from "react";
import AppHeader from "@/components/AppHeader";
import BottomNav from "@/components/BottomNav";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageSquare, Send, Plus, Trash2, Clock, Phone,
  ChevronDown, ChevronUp, Loader2, X, ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  reminder: { bg: "#FEF3C7", text: "#92400E" },
  update: { bg: "#DBEAFE", text: "#1E40AF" },
  booking: { bg: "#D1FAE5", text: "#065F46" },
  payment: { bg: "#FEE2E2", text: "#991B1B" },
  custom: { bg: "#F3E8FF", text: "#6B21A8" },
};

export default function SmsPage() {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [smsCredits, setSmsCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("templates");
  const [sendModal, setSendModal] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [tplRes, histRes, balRes] = await Promise.all([
        axios.get(`${API}/sms/templates`, { withCredentials: true }),
        axios.get(`${API}/sms/history`, { withCredentials: true }),
        axios.get(`${API}/sms/balance`, { withCredentials: true }),
      ]);
      setTemplates(tplRes.data.templates || []);
      setHistory(histRes.data.history || []);
      setSmsCredits(balRes.data.sms_credits || 0);
    } catch {
      toast.error("Failed to load SMS data");
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/sms/templates/${id}`, { withCredentials: true });
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete template");
    }
  };

  const sendFromTemplate = async (templateId, to, variables) => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/sms/send-template`, {
        template_id: templateId, to, variables,
      }, { withCredentials: true });
      setSmsCredits(data.sms_credits);
      toast.success(data.mocked ? "SMS queued (test mode)" : "SMS sent successfully!");
      setSendModal(null);
      // Refresh history
      const histRes = await axios.get(`${API}/sms/history`, { withCredentials: true });
      setHistory(histRes.data.history || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
      <AppHeader />
      <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00A3E0] animate-spin" /></div>
    </div>
  );

  return (
    <div className="min-h-screen pb-16 sm:pb-0" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="sms-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="p-2 rounded-xl transition-colors hover:bg-gray-100" style={{ color: 'var(--text-secondary)' }} data-testid="sms-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }} data-testid="sms-title">
                SMS Centre
              </h1>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>Send reminders to homeowners in one tap</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid="sms-credit-badge">
            <MessageSquare className="w-4 h-4 text-[#D97706]" />
            <span className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{smsCredits}</span>
            <span className="text-[10px] ml-0.5" style={{ color: 'var(--text-secondary)' }}>credits</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b" style={{ borderColor: 'var(--border-main)' }}>
          {[
            { id: "templates", label: "Templates", icon: MessageSquare },
            { id: "history", label: "History", icon: Clock },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sms-tab-${t.id}`}
              className="px-5 py-2.5 text-sm font-semibold transition-all -mb-[1px] border-b-2"
              style={{
                fontFamily: 'Outfit, sans-serif',
                borderColor: tab === t.id ? '#00A3E0' : 'transparent',
                color: tab === t.id ? '#00A3E0' : 'var(--text-secondary)',
              }}>
              <t.icon className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Templates Tab */}
        {tab === "templates" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                {templates.length} template{templates.length !== 1 ? "s" : ""} available
              </p>
              <button onClick={() => setCreateOpen(true)} data-testid="create-template-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors">
                <Plus className="w-4 h-4" /> New Template
              </button>
            </div>

            <div className="space-y-3" data-testid="templates-list">
              {templates.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  template={tpl}
                  onSend={() => setSendModal(tpl)}
                  onDelete={() => deleteTemplate(tpl.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* History Tab */}
        {tab === "history" && (
          <div>
            {history.length === 0 ? (
              <div className="rounded-2xl text-center py-12 px-6" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid="sms-history-empty">
                <Clock className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>No messages yet</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Send your first SMS using a template above.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="sms-history-list">
                {history.map((log, i) => (
                  <div key={i} className="rounded-2xl p-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid={`sms-log-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F7FC' }}>
                          <Send className="w-4 h-4 text-[#00A3E0]" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{log.to}</span>
                          {log.template_name && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3E8FF', color: '#6B21A8' }}>{log.template_name}</span>
                          )}
                          {log.mocked && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">TEST</span>
                          )}
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{log.message}</p>
                      </div>
                      <span className="text-[10px] flex-shrink-0 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        {log.sent_at ? new Date(log.sent_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send Modal */}
      {sendModal && (
        <SendModal
          template={sendModal}
          sending={sending}
          onClose={() => setSendModal(null)}
          onSend={sendFromTemplate}
        />
      )}

      {/* Create Template Modal */}
      {createOpen && (
        <CreateTemplateModal
          onClose={() => setCreateOpen(false)}
          onCreate={async (name, body, category) => {
            try {
              const { data } = await axios.post(`${API}/sms/templates`, { name, body, category }, { withCredentials: true });
              setTemplates((prev) => [...prev, data]);
              setCreateOpen(false);
              toast.success("Template created!");
            } catch {
              toast.error("Failed to create template");
            }
          }}
        />
      )}

      <BottomNav />
    </div>
  );
}

function TemplateCard({ template: tpl, onSend, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const colors = CATEGORY_COLORS[tpl.category] || CATEGORY_COLORS.custom;
  const variables = (tpl.body.match(/\{(\w+)\}/g) || []).map((v) => v.slice(1, -1));

  return (
    <div className="rounded-2xl overflow-hidden transition-all" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid={`template-card-${tpl.id}`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left transition-colors hover:opacity-90">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.bg }}>
          <MessageSquare className="w-5 h-5" style={{ color: colors.text }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>{tpl.name}</p>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors.bg, color: colors.text }}>
              {tpl.category}
            </span>
            {tpl.is_system && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-muted)' }}>
                default
              </span>
            )}
          </div>
          <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-secondary)' }}>{tpl.body}</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
          <div className="rounded-xl p-3 mt-3 text-sm leading-relaxed" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-primary)' }}>
            {tpl.body}
          </div>
          {variables.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>Variables:</span>
              {variables.map((v) => (
                <span key={v} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>{`{${v}}`}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button onClick={onSend} data-testid={`send-template-${tpl.id}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors">
              <Send className="w-3.5 h-3.5" /> Send SMS
            </button>
            {!tpl.is_system && (
              <button onClick={onDelete} data-testid={`delete-template-${tpl.id}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SendModal({ template, sending, onClose, onSend }) {
  const [phone, setPhone] = useState("");
  const variables = (template.body.match(/\{(\w+)\}/g) || []).map((v) => v.slice(1, -1));
  const [vars, setVars] = useState(() => {
    const init = {};
    variables.forEach((v) => { init[v] = ""; });
    return init;
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const preview = (() => {
    let msg = template.body;
    for (const [k, v] of Object.entries(vars)) {
      msg = msg.replace(new RegExp(`\\{${k}\\}`, "g"), v || `{${k}}`);
    }
    return msg;
  })();

  const handleSend = () => {
    if (!phone.trim()) { toast.error("Please enter a phone number"); return; }
    onSend(template.id, phone.trim(), vars);
  };

  const cleanup = () => { document.body.style.overflow = ""; onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="send-sms-modal">
      <div className="fixed inset-0 bg-black/40" onClick={cleanup} />
      <div className="relative w-full max-w-md mx-4 mb-0 sm:mb-0 rounded-t-2xl sm:rounded-2xl overflow-hidden animate-fade-in-up" style={{ backgroundColor: 'var(--bg-card)' }}
        onTouchMove={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
          <div>
            <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Send SMS</h3>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{template.name}</p>
          </div>
          <button onClick={cleanup} className="p-1 rounded-lg hover:bg-gray-100" data-testid="close-send-modal"><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: '60vh', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {/* Phone */}
          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Recipient Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              <input
                data-testid="sms-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07450 123 456"
                className="w-full rounded-xl border px-3 pl-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0]"
                style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>

          {/* Variable inputs */}
          {variables.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Fill in details</p>
              {variables.map((v) => (
                <div key={v}>
                  <label className="text-xs font-medium block mb-1 capitalize" style={{ color: 'var(--text-secondary)' }}>{v}</label>
                  <input
                    data-testid={`sms-var-${v}`}
                    type="text"
                    value={vars[v]}
                    onChange={(e) => setVars((prev) => ({ ...prev, [v]: e.target.value }))}
                    placeholder={v}
                    className="w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0]"
                    style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Preview */}
          <div>
            <p className="text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Preview</p>
            <div className="rounded-xl p-3 text-sm leading-relaxed" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-primary)' }} data-testid="sms-preview">
              {preview}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>1 credit will be used</span>
          <button onClick={handleSend} disabled={sending} data-testid="confirm-send-sms"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending..." : "Send SMS"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateTemplateModal({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("custom");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Template name is required"); return; }
    if (!body.trim()) { toast.error("Message body is required"); return; }
    setSaving(true);
    await onCreate(name, body, category);
    setSaving(false);
  };

  const cleanup = () => { document.body.style.overflow = ""; onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="create-template-modal">
      <div className="fixed inset-0 bg-black/40" onClick={cleanup} />
      <div className="relative w-full max-w-md mx-4 mb-0 sm:mb-0 rounded-t-2xl sm:rounded-2xl overflow-hidden animate-fade-in-up" style={{ backgroundColor: 'var(--bg-card)' }}
        onTouchMove={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
          <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>New Template</h3>
          <button onClick={cleanup} className="p-1 rounded-lg hover:bg-gray-100" data-testid="close-create-modal"><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Template Name</label>
            <input
              data-testid="template-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Follow Up"
              className="w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0]"
              style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
            />
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Category</label>
            <select
              data-testid="template-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0]"
              style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
            >
              <option value="custom">Custom</option>
              <option value="reminder">Reminder</option>
              <option value="update">Update</option>
              <option value="booking">Booking</option>
              <option value="payment">Payment</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>
              Message Body
              <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>Use {`{name}`}, {`{address}`}, {`{date}`}, {`{phone}`}, {`{amount}`} as variables</span>
            </label>
            <textarea
              data-testid="template-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Hi {name}, your appointment is confirmed for {date} at {address}."
              className="w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0] resize-none"
              style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
            />
            <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{body.length} / 160 characters</p>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border-light)' }}>
          <button onClick={cleanup} className="px-4 py-2 rounded-xl text-sm font-semibold transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="cancel-create-template">
            Cancel
          </button>
          <button onClick={handleCreate} disabled={saving} data-testid="save-template-btn"
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {saving ? "Creating..." : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
}
